<template>
    <div
        v-if="blok"
        v-editable="blok"
        class="multi-column-layout"
        data-test="multi-column-layout"
        :class="layoutClasses"
        :style="[layoutStyles]"
    >
        <NuxtImg
            v-if="props.blok.BackgroundImage?.filename"
            :src="props.blok.BackgroundImage?.filename"
            :alt="props.blok.BackgroundImage?.alt"
            provider="storyblok"
            sizes="sm:200vw md:150vw lg:100vw xl:1920px"
            format="webp"
            class="bg-image"
            densities="x1 x2"
            loading="lazy"
        />
        <div class="mx-auto main-wrapper" :class="{ container: !blok.fullWidth }">
            <component
                :is="blok.titleHeadingType ? blok.titleHeadingType : 'h2'"
                v-if="blok.title && blok.showTitle"
                class="section-title"
                :style="titleStyles"
                :class="[{ 'hide-on-mobile': hideTitle }, blok.TitleAlign]"
            >
                {{ blok.title }}
            </component>
            <div
                v-if="blok.subtitle && blok.showTitle"
                class="section-subtitle"
                :class="blok.TextAlign"
                :style="`${blok.TitleColor ? `color: ${blok.TitleColor?.color};` : ''} ${blok.TextAlign ? `text-align: ${blok.TextAlign};` : ''}`"
                v-html="blok.subtitle"
            ></div>

            <div class="grid grid-cols-1 md:grid-cols-12 grid-wrapper">
                <template v-for="slot in blok.slots" :key="slot._uid">
                    <div
                        v-if="slot"
                        :style="[getSlotStyles(slot)]"
                        class="flex slot"
                        :class="[`justify-${slot.text_align} items-${slot.align_items}`, `columns-${slot.columns}`]"
                    >
                        <template v-for="content in slot.content" :key="content._uid">
                            <Component
                                :is="content.component"
                                :blok="content"
                                class="w-full"
                                :class="{ 'mob-hidden': content.hide_on_mobile || slot.hide_on_mobile }"
                            />
                        </template>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { IMultiColumnsLayout, ISlot } from '~ui/types/components/MultiColumnsLayout';
import { useIsMobileView } from '~ui/composables/isMobileView';

const props: any = defineProps<{ blok: IMultiColumnsLayout }>();

const { isMobileView } = useIsMobileView('768px');

const hideTitle: any = computed(() => {
    props.blok.slots.every((slot: any) => slot.hide_on_mobile);
});

const layoutStyles = computed(() => {
    const hasGradient = props.blok.backgroundGradient1?.color && props.blok.backgroundGradient2?.color;
    const gradient = `linear-gradient(180deg, ${props.blok.backgroundGradient1?.color} 0%, ${props.blok.backgroundGradient2?.color} 100%)`;
    const backgroundColor = props.blok.BackgroundColor?.color;

    return {
        background: hasGradient ? gradient : backgroundColor,
        paddingTop: props.blok.paddingTop ? `${props.blok.paddingTop}px` : null,
        paddingBottom: props.blok.paddingBottom ? `${props.blok.paddingBottom}px` : null,
    };
});

const layoutClasses = computed(() => ({
    dividers: props.blok.ShowDividers,
    'hide-on-desktop': props.blok.displayOption === 'only-mobile',
    'hide-on-mobile': props.blok.displayOption === 'only-desktop',
    [props.blok.customClass]: props.blok.customClass,
    'has-background-image': props.blok.BackgroundImage?.filename,
}));

const titleStyles = computed(() => ({
    color: props.blok.TitleColor ? props.blok.TitleColor?.color : null,
    fontWeight: props.blok.TitleFontWeight ? props.blok.TitleFontWeight : null,
    fontSize: !isMobileView.value && props.blok.TitleFontSize ? `${props.blok.TitleFontSize}rem` : null,
    lineHeight: !isMobileView.value && props.blok.TitleFontSize ? `${props.blok.TitleFontSize}rem` : null,
    textAlign: props.blok.TextAlign ? props.blok.TextAlign : null,
}));

const getSlotStyles = (slot: ISlot) => {
    return `grid-column: span ${slot.columns} / span ${slot.columns};
                        ${slot.direction ? `flex-direction: ${slot.direction}` : ''};
                        ${slot.PaddingTop ? `padding-top: ${slot.PaddingTop}px` : ''};
                        ${slot.PaddingBottom ? `padding-bottom: ${slot.PaddingBottom}px` : ''};`;
};
</script>

<style scoped lang="postcss">
.container {
    @apply mx-auto px-4;
}

.multi-column-layout {
    @apply relative py-6 max-md:py-4;

    .bg-image {
        @apply absolute top-0 left-0 w-full h-full object-cover object-center;
    }

    .section-title {
        @apply font-normal text-center mb-4 text-2xl;
    }

    .section-subtitle {
        @apply font-normal text-center mb-12 py-2 text-sm;
    }

    &.dividers {
        border-top: 1px solid #e5e7eb;
        border-bottom: 1px solid #e5e7eb;
    }
    &.hide-on-mobile {
        @apply hidden md:block;
    }
    &.hide-on-desktop {
        @apply block md:hidden;
    }

    .slot {
        @apply max-md:col-auto !important;
    }
}

.multi-column-layout {
    .mob-hidden,
    .hide-on-mobile {
        @apply hidden md:block;
    }
    &.has-background-image {
        :deep(h2) {
            @media screen and (max-width: 768px) {
                text-shadow: #fff 1px 0 10px;
            }
        }
    }
}
</style>
